import { Check, ClipboardList, Copy, Database } from 'lucide-react';
import { useState } from 'react';
import ReportSection from './ReportSection';
import { tx } from '../../i18n';
import { formatDateTime } from '../../lib/format';
import { updatedLabel } from '../../lib/report';
import type { ChecklistItem } from '../../lib/report';
import type { AnalysisDetailResponse, SourceStatusView } from '../../types/api';

function statusCopy(status: SourceStatusView['status']): string {
  switch (status) {
    case 'OK':
      return tx('Live', 'Canlı',
    'Na żywo',
    'Em direto',
    'En directo');
    case 'PARTIAL':
      return tx('Partial', 'Kısmi',
    'Częściowe',
    'Parcial',
    'Parcial');
    case 'UNAVAILABLE':
      return tx('Unavailable', 'Alınamadı',
    'Niedostępne',
    'Indisponível',
    'No disponible');
    default:
      return tx('Simulated', 'Örnek veri',
    'Dane demonstracyjne',
    'Dados de demonstração',
    'Datos de demostración');
  }
}

/**
 * Which sources fed the report and how each behaved when it was asked. Folded away like every
 * other section, so the evidence no longer needs a collapse of its own.
 */
export function SourcesCard({ detail }: { detail: AnalysisDetailResponse }) {
  const names = detail.sources.map((source) => source.sourceName);

  return (
    <ReportSection
      id="section-sources"
      icon={Database}
      title={tx('Sources and limitations', 'Kaynaklar ve sınırlamalar',
    'Źródła i ograniczenia',
    'Fontes e limitações',
    'Fuentes y limitaciones')}
      note={`${names.join(', ')}${names.length > 0 ? ' · ' : ''}${updatedLabel(detail.generatedAt, true)}`}
    >
      <dl className="sources-card__list">
        {detail.sources.map((source) => (
          <div key={source.sourceName} className="definition">
            <dt>
              {source.sourceName}
              {source.note ? <span className="tiny muted" style={{ display: 'block' }}>{source.note}</span> : null}
            </dt>
            <dd>
              <span className={source.status === 'OK' ? 'status status--ok' : 'status'}>
                {statusCopy(source.status)}
              </span>
              {source.datasetVersion ? <span className="tiny muted"> {source.datasetVersion}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
      <p className="report-disclaimer small muted">
        {tx(
          'ScanHome describes an address, its surroundings and the certificates registered against it, from public data that is sometimes incomplete or out of date. It is not a survey, not a valuation, and not advice on whether to take a tenancy - that judgement is yours, and nothing here replaces viewing the home and reading the agreement.',
          'ScanHome; bir adresi, çevresini ve adına kayıtlı sertifikaları, zaman zaman eksik ya da güncel olmayabilen kamu verilerinden aktarır. Bir ekspertiz, bir değerleme ya da kiralama tavsiyesi değildir; karar sizindir ve buradaki hiçbir şey konutu görmenin ve sözleşmeyi okumanın yerini tutmaz.',
          'ScanHome opisuje adres, jego okolicę i świadectwa zarejestrowane pod nim, na podstawie danych publicznych, które bywają niekompletne lub nieaktualne. To nie jest ekspertyza, wycena ani porada, czy podpisać najem — decyzja należy do Ciebie i nic tu nie zastąpi obejrzenia mieszkania i przeczytania umowy.',
          'O ScanHome descreve uma morada, o que a rodeia e os certificados registados em seu nome, a partir de dados públicos por vezes incompletos ou desatualizados. Não é uma peritagem, nem uma avaliação, nem um conselho sobre aceitar o arrendamento — essa decisão é sua, e nada aqui substitui ver a casa e ler o contrato.',
          'ScanHome describe una dirección, su entorno y los certificados registrados a su nombre, a partir de datos públicos que a veces están incompletos o desactualizados. No es una tasación, ni una peritación, ni un consejo sobre firmar el alquiler: esa decisión es tuya, y nada de esto sustituye a ver la vivienda y leer el contrato.',
        )}
      </p>
      <p className="tiny muted">
        {tx('Report', 'Rapor',
    'Raport',
    'Relatório',
    'Informe')} {detail.analysisId}
        {detail.accessExpires
          ? tx(` · access expires ${formatDateTime(detail.accessExpires)}`, ` · erişim bitişi ${formatDateTime(detail.accessExpires)}`,
    ` · dostęp wygasa ${formatDateTime(detail.accessExpires)}`,
    ` · o acesso expira em ${formatDateTime(detail.accessExpires)}`,
    ` · el acceso caduca el ${formatDateTime(detail.accessExpires)}`)
          : ''}
      </p>
    </ReportSection>
  );
}

/** Numbered things to settle with the agent before paying anything, each copyable as a message. */
export function ChecklistCard({ items }: { items: ChecklistItem[] }) {
  const [copied, setCopied] = useState<string | null>(null);

  if (items.length === 0) {
    return null;
  }

  const copy = async (item: ChecklistItem) => {
    try {
      await navigator.clipboard.writeText(item.detail);
      setCopied(item.category);
      window.setTimeout(() => setCopied((current) => (current === item.category ? null : current)), 1800);
    } catch {
      setCopied(null);
    }
  };

  return (
    <ReportSection
      id="checklist"
      icon={ClipboardList}
      /*
       * Not "questions to ask the landlord": the same report is read by someone buying as well as
       * someone renting, and by someone deciding whether to view at all. The questions are the
       * same either way - what the registers leave open about this building - so the heading says
       * when to ask them rather than who to ask.
       */
      title={tx(
        'Before you commit to this home',
        'Bu konutla ilgili işlem yapmadan önce',
        'Zanim zdecydujesz się na to mieszkanie',
        'Antes de avançar com esta casa',
        'Antes de comprometerte con esta vivienda',
      )}
      note={tx(
        `${items.length} things to settle first. Each one can be sent to the landlord, agent or seller as it is written.`,
        `Önce netleştirilecek ${items.length} konu. Her biri ev sahibine, emlakçıya ya da satıcıya yazıldığı gibi gönderilebilir.`,
        `${items.length} spraw do wyjaśnienia. Każdą można wysłać właścicielowi, pośrednikowi lub sprzedającemu w tej formie.`,
        `${items.length} pontos a esclarecer. Cada um pode ser enviado ao senhorio, à agência ou ao vendedor tal como está.`,
        `${items.length} puntos que aclarar. Cada uno se puede enviar al propietario, la agencia o el vendedor tal cual.`,
      )}
    >
      <ol className="checklist">
        {items.map((item, index) => (
          <li key={item.category} className="checklist__item">
            <span className="checklist__number">{index + 1}</span>
            <span className="checklist__text">
              <span className="checklist__title">{item.title}</span>
              <span className="tiny muted">{item.detail}</span>
            </span>
            <button type="button" className="link-button" onClick={() => copy(item)}>
              {copied === item.category ? (
                <>
                  <Check size={13} aria-hidden="true" /> {tx('Copied', 'Kopyalandı',
    'Skopiowano',
    'Copiado',
    'Copiado')}
                </>
              ) : (
                <>
                  <Copy size={13} aria-hidden="true" /> {tx('Copy question', 'Soruyu kopyala',
    'Kopiuj pytanie',
    'Copiar a pergunta',
    'Copiar la pregunta')}
                </>
              )}
            </button>
          </li>
        ))}
      </ol>
    </ReportSection>
  );
}
